/* eslint-disable indent */
import produce from 'immer';

import {
  SHOWROOM_FETCH_EVENTS,
  SHOWROOM_FETCH_EVENTS_SUCCESS,
  SHOWROOM_FETCH_EVENTS_FAILED,
  SHOWROOM_FETCH_EVENT_DETAIL,
  SHOWROOM_FETCH_EVENT_DETAIL_SUCCESS,
  SHOWROOM_FETCH_EVENT_DETAIL_FAILED,
  SHOWROOM_UPDATE_EVENT,
  SHOWROOM_UPDATE_EVENT_SUCCESS,
  SHOWROOM_UPDATE_EVENT_FAILED,
  SHOWROOM_FETCH_MEMBERS,
  SHOWROOM_FETCH_MEMBERS_SUCCESS,
  SHOWROOM_FETCH_MEMBERS_FAILED,
  SHOWROOM_REVIEW_HOST,
  SHOWROOM_REVIEW_HOST_SUCCESS,
  SHOWROOM_REVIEW_HOST_FAILED,
  SHOWROOM_FETCH_VIEWERS,
  SHOWROOM_FETCH_VIEWERS_SUCCESS,
  SHOWROOM_FETCH_VIEWERS_FAILED,
  SHOWROOM_FETCH_ACCESS_REQUESTS,
  SHOWROOM_FETCH_ACCESS_REQUESTS_SUCCESS,
  SHOWROOM_FETCH_ACCESS_REQUESTS_FAILED,
  SHOWROOM_REVIEW_ACCESS_REQUEST,
  SHOWROOM_REVIEW_ACCESS_REQUEST_SUCCESS,
  SHOWROOM_REVIEW_ACCESS_REQUEST_FAILED,
  SHOWROOM_FETCH_CHAT_MESSAGES,
  SHOWROOM_FETCH_CHAT_MESSAGES_SUCCESS,
  SHOWROOM_FETCH_CHAT_MESSAGES_FAILED,
  SHOWROOM_DELETE_CHAT_MESSAGE,
  SHOWROOM_DELETE_CHAT_MESSAGE_SUCCESS,
  SHOWROOM_DELETE_CHAT_MESSAGE_FAILED,
  SHOWROOM_FETCH_ELIGIBILITY,
  SHOWROOM_FETCH_ELIGIBILITY_SUCCESS,
  SHOWROOM_FETCH_ELIGIBILITY_FAILED,
  SHOWROOM_FETCH_PRICING_PACKAGES,
  SHOWROOM_FETCH_PRICING_PACKAGES_SUCCESS,
  SHOWROOM_FETCH_PRICING_PACKAGES_FAILED,
  SHOWROOM_UPDATE_PRICING_PACKAGES,
  SHOWROOM_UPDATE_PRICING_PACKAGES_SUCCESS,
  SHOWROOM_UPDATE_PRICING_PACKAGES_FAILED,
  SHOWROOM_PUBLISH_EVENT,
  SHOWROOM_PUBLISH_EVENT_SUCCESS,
  SHOWROOM_PUBLISH_EVENT_FAILED,
  SHOWROOM_UNPUBLISH_EVENT,
  SHOWROOM_UNPUBLISH_EVENT_SUCCESS,
  SHOWROOM_UNPUBLISH_EVENT_FAILED,
  SHOWROOM_DELETE_EVENT,
  SHOWROOM_DELETE_EVENT_SUCCESS,
  SHOWROOM_DELETE_EVENT_FAILED,
  SHOWROOM_ADJUST_SEATS,
  SHOWROOM_ADJUST_SEATS_SUCCESS,
  SHOWROOM_ADJUST_SEATS_FAILED,
  SHOWROOM_ADMIN_REVIEW_EVENT,
  SHOWROOM_ADMIN_REVIEW_EVENT_SUCCESS,
  SHOWROOM_ADMIN_REVIEW_EVENT_FAILED,
  SHOWROOM_BATCH_FREE_MEMBER_PASS,
  SHOWROOM_BATCH_FREE_MEMBER_PASS_SUCCESS,
  SHOWROOM_BATCH_FREE_MEMBER_PASS_FAILED,
  SHOWROOM_SET_ACTIVE_TAB,
  SHOWROOM_CLEAR_CURRENT_EVENT,
} from 'actions/showroomActions';

export const initialState = {
  events: [],
  eventsFetching: false,
  eventsSuccess: false,
  eventsFailed: false,
  eventsError: null,
  eventsTotal: 0,
  eventsPage: 0,
  currentEvent: null,
  currentEventFetching: false,
  currentEventSuccess: false,
  currentEventFailed: false,
  currentEventError: null,
  members: [],
  membersFetching: false,
  membersSuccess: false,
  membersFailed: false,
  membersTotal: 0,
  viewers: [],
  viewersFetching: false,
  viewersSuccess: false,
  viewersFailed: false,
  viewersTotal: 0,
  viewersWatchStats: null,
  accessRequests: [],
  accessRequestsFetching: false,
  accessRequestsSuccess: false,
  accessRequestsFailed: false,
  chatMessages: [],
  chatMessagesFetching: false,
  chatMessagesSuccess: false,
  chatMessagesFailed: false,
  memberPasses: [],
  memberPassesFetching: false,
  memberPassesSuccess: false,
  memberPassesFailed: false,
  eligibility: null,
  eligibilityFetching: false,
  eligibilitySuccess: false,
  eligibilityFailed: false,
  eligibilityError: null,
  pricingPackages: [],
  pricingPackagesFetching: false,
  pricingPackagesSuccess: false,
  pricingPackagesFailed: false,
  pricingPackagesError: null,
  pricingPackagesUpdating: false,
  pricingPackagesUpdateFailed: false,
  pricingPackagesUpdateError: null,
  reviewSubmitting: false,
  reviewSuccess: false,
  reviewFailed: false,
  reviewError: null,
  eventDeleteSubmitting: false,
  eventDeleteSuccess: false,
  eventDeleteFailed: false,
  eventDeleteError: null,
  activeTab: 'events',
};

/* eslint-disable default-case, no-param-reassign */
const updateEventInList = (events, event) =>
  events.map((e) => (e._id === event._id ? {...e, ...event} : e));

const getActionError = (action, fallback) =>
  action.err?.message ||
  action.err?.error ||
  action.err?.response?.message ||
  fallback;

const showroomReducer = (state = initialState, action) =>
  // eslint-disable-next-line consistent-return
  produce(state, (draft) => {
    switch (action.type) {
      // ========================
      // Events
      // ========================
      case SHOWROOM_FETCH_EVENTS:
        draft.eventsFetching = true;
        draft.eventsSuccess = false;
        draft.eventsFailed = false;
        draft.eventsError = null;
        draft.events = [];
        break;

      case SHOWROOM_FETCH_EVENTS_SUCCESS:
        draft.eventsFetching = false;
        draft.eventsSuccess = true;
        draft.eventsFailed = false;
        draft.eventsError = null;
        draft.events = action.events;
        draft.eventsTotal = action.pagination ? action.pagination.total : 0;
        draft.eventsPage = action.pagination ? action.pagination.pageNumber : 0;
        break;

      case SHOWROOM_FETCH_EVENTS_FAILED:
        draft.eventsFetching = false;
        draft.eventsSuccess = false;
        draft.eventsFailed = true;
        draft.eventsError = getActionError(
          action,
          'Show rooms could not be loaded'
        );
        break;

      case SHOWROOM_FETCH_EVENT_DETAIL:
        draft.currentEventFetching = true;
        draft.currentEventSuccess = false;
        draft.currentEventFailed = false;
        draft.currentEventError = null;
        draft.currentEvent = null;
        break;

      case SHOWROOM_FETCH_EVENT_DETAIL_SUCCESS:
        draft.currentEventFetching = false;
        draft.currentEventSuccess = true;
        draft.currentEventFailed = false;
        draft.currentEventError = null;
        draft.currentEvent = action.event;
        break;

      case SHOWROOM_FETCH_EVENT_DETAIL_FAILED:
        draft.currentEventFetching = false;
        draft.currentEventSuccess = false;
        draft.currentEventFailed = true;
        draft.currentEventError = getActionError(
          action,
          'Show room details could not be loaded'
        );
        break;

      case SHOWROOM_UPDATE_EVENT:
        draft.currentEventFetching = true;
        draft.currentEventSuccess = false;
        draft.currentEventFailed = false;
        draft.currentEventError = null;
        break;

      case SHOWROOM_UPDATE_EVENT_SUCCESS:
        draft.currentEventFetching = false;
        draft.currentEventSuccess = true;
        draft.currentEventFailed = false;
        draft.currentEventError = null;
        draft.currentEvent = action.event;
        draft.events = updateEventInList(draft.events, action.event);
        break;

      case SHOWROOM_UPDATE_EVENT_FAILED:
        draft.currentEventFetching = false;
        draft.currentEventSuccess = false;
        draft.currentEventFailed = true;
        draft.currentEventError = getActionError(
          action,
          'Show room could not be updated'
        );
        break;

      // ========================
      // Members (admin)
      // ========================
      case SHOWROOM_FETCH_MEMBERS:
        draft.membersFetching = true;
        draft.membersSuccess = false;
        draft.membersFailed = false;
        draft.members = [];
        break;

      case SHOWROOM_FETCH_MEMBERS_SUCCESS:
        draft.membersFetching = false;
        draft.membersSuccess = true;
        draft.membersFailed = false;
        draft.members = action.members;
        draft.membersTotal = action.pagination ? action.pagination.total : 0;
        break;

      case SHOWROOM_FETCH_MEMBERS_FAILED:
        draft.membersFetching = false;
        draft.membersSuccess = false;
        draft.membersFailed = true;
        break;

      // ========================
      // Host Approval (admin)
      // ========================
      case SHOWROOM_REVIEW_HOST:
        draft.reviewSubmitting = true;
        draft.reviewSuccess = false;
        draft.reviewFailed = false;
        draft.reviewError = null;
        break;

      case SHOWROOM_REVIEW_HOST_SUCCESS:
        draft.reviewSubmitting = false;
        draft.reviewSuccess = true;
        draft.reviewFailed = false;
        draft.reviewError = null;
        draft.members = draft.members.map((m) => {
          const matchId = action.member._id || action.member.companyId;
          if (m._id === matchId || m.companyId === matchId) {
            return { ...m, ...action.member };
          }
          return m;
        });
        break;

      case SHOWROOM_REVIEW_HOST_FAILED:
        draft.reviewSubmitting = false;
        draft.reviewSuccess = false;
        draft.reviewFailed = true;
        draft.reviewError = action.err?.message || action.err?.error || 'Host review failed';
        break;

      // ========================
      // Viewers
      // ========================
      case SHOWROOM_FETCH_VIEWERS:
        draft.viewersFetching = true;
        draft.viewersSuccess = false;
        draft.viewersFailed = false;
        draft.viewers = [];
        draft.viewersTotal = 0;
        draft.viewersWatchStats = null;
        break;

      case SHOWROOM_FETCH_VIEWERS_SUCCESS:
        draft.viewersFetching = false;
        draft.viewersSuccess = true;
        draft.viewersFailed = false;
        draft.viewers = action.viewers;
        draft.viewersTotal = action.pagination ? action.pagination.total : 0;
        draft.viewersWatchStats = action.pagination
          ? action.pagination.watchStats || null
          : null;
        break;

      case SHOWROOM_FETCH_VIEWERS_FAILED:
        draft.viewersFetching = false;
        draft.viewersSuccess = false;
        draft.viewersFailed = true;
        break;

      // ========================
      // Access Requests (host)
      // ========================
      case SHOWROOM_FETCH_ACCESS_REQUESTS:
        draft.accessRequestsFetching = true;
        draft.accessRequestsSuccess = false;
        draft.accessRequestsFailed = false;
        draft.accessRequests = [];
        break;

      case SHOWROOM_FETCH_ACCESS_REQUESTS_SUCCESS:
        draft.accessRequestsFetching = false;
        draft.accessRequestsSuccess = true;
        draft.accessRequestsFailed = false;
        draft.accessRequests = action.accessRequests;
        break;

      case SHOWROOM_FETCH_ACCESS_REQUESTS_FAILED:
        draft.accessRequestsFetching = false;
        draft.accessRequestsSuccess = false;
        draft.accessRequestsFailed = true;
        break;

      case SHOWROOM_REVIEW_ACCESS_REQUEST:
        draft.accessRequestsFetching = true;
        draft.accessRequestsSuccess = false;
        draft.accessRequestsFailed = false;
        break;

      case SHOWROOM_REVIEW_ACCESS_REQUEST_SUCCESS:
        draft.accessRequestsFetching = false;
        draft.accessRequestsSuccess = true;
        draft.accessRequestsFailed = false;
        draft.accessRequests = draft.accessRequests.map((existingRequest) => {
          if (existingRequest._id !== action.accessRequest._id) {
            return existingRequest;
          }
          const nextAccessRequest = {
            ...existingRequest,
            ...action.accessRequest,
          };
          if (existingRequest.user || action.accessRequest.user) {
            nextAccessRequest.user = {
              ...(existingRequest.user || {}),
              ...(action.accessRequest.user || {}),
              name:
                action.accessRequest.user?.name ||
                existingRequest.user?.name ||
                '',
            };
          }
          return nextAccessRequest;
        });
        break;

      case SHOWROOM_REVIEW_ACCESS_REQUEST_FAILED:
        draft.accessRequestsFetching = false;
        draft.accessRequestsSuccess = false;
        draft.accessRequestsFailed = true;
        break;

      // ========================
      // Chat Messages
      // ========================
      case SHOWROOM_FETCH_CHAT_MESSAGES:
        draft.chatMessagesFetching = true;
        draft.chatMessagesSuccess = false;
        draft.chatMessagesFailed = false;
        draft.chatMessages = [];
        break;

      case SHOWROOM_FETCH_CHAT_MESSAGES_SUCCESS:
        draft.chatMessagesFetching = false;
        draft.chatMessagesSuccess = true;
        draft.chatMessagesFailed = false;
        draft.chatMessages = action.messages;
        break;

      case SHOWROOM_FETCH_CHAT_MESSAGES_FAILED:
        draft.chatMessagesFetching = false;
        draft.chatMessagesSuccess = false;
        draft.chatMessagesFailed = true;
        break;

      case SHOWROOM_DELETE_CHAT_MESSAGE:
        draft.chatMessagesFetching = true;
        draft.chatMessagesSuccess = false;
        draft.chatMessagesFailed = false;
        break;

      case SHOWROOM_DELETE_CHAT_MESSAGE_SUCCESS:
        draft.chatMessagesFetching = false;
        draft.chatMessagesSuccess = true;
        draft.chatMessagesFailed = false;
        draft.chatMessages = draft.chatMessages.filter(
          (m) => m._id !== action.messageId
        );
        break;

      case SHOWROOM_DELETE_CHAT_MESSAGE_FAILED:
        draft.chatMessagesFetching = false;
        draft.chatMessagesSuccess = false;
        draft.chatMessagesFailed = true;
        break;

      // ========================
      // Eligibility
      // ========================
      case SHOWROOM_FETCH_ELIGIBILITY:
        draft.eligibilityFetching = true;
        draft.eligibilitySuccess = false;
        draft.eligibilityFailed = false;
        draft.eligibilityError = null;
        draft.eligibility = null;
        break;

      case SHOWROOM_FETCH_ELIGIBILITY_SUCCESS:
        draft.eligibilityFetching = false;
        draft.eligibilitySuccess = true;
        draft.eligibilityFailed = false;
        draft.eligibilityError = null;
        draft.eligibility = action.eligibility;
        break;

      case SHOWROOM_FETCH_ELIGIBILITY_FAILED:
        draft.eligibilityFetching = false;
        draft.eligibilitySuccess = false;
        draft.eligibilityFailed = true;
        draft.eligibilityError = getActionError(
          action,
          'Showroom access could not be loaded'
        );
        break;

      case SHOWROOM_FETCH_PRICING_PACKAGES:
        draft.pricingPackagesFetching = true;
        draft.pricingPackagesSuccess = false;
        draft.pricingPackagesFailed = false;
        draft.pricingPackagesError = null;
        break;

      case SHOWROOM_FETCH_PRICING_PACKAGES_SUCCESS:
        draft.pricingPackagesFetching = false;
        draft.pricingPackagesSuccess = true;
        draft.pricingPackagesFailed = false;
        draft.pricingPackagesError = null;
        draft.pricingPackages = action.packages;
        break;

      case SHOWROOM_FETCH_PRICING_PACKAGES_FAILED:
        draft.pricingPackagesFetching = false;
        draft.pricingPackagesSuccess = false;
        draft.pricingPackagesFailed = true;
        draft.pricingPackagesError = getActionError(
          action,
          'Pricing packages could not be loaded'
        );
        break;

      case SHOWROOM_UPDATE_PRICING_PACKAGES:
        draft.pricingPackagesUpdating = true;
        draft.pricingPackagesUpdateFailed = false;
        draft.pricingPackagesUpdateError = null;
        break;

      case SHOWROOM_UPDATE_PRICING_PACKAGES_SUCCESS:
        draft.pricingPackagesUpdating = false;
        draft.pricingPackagesSuccess = true;
        draft.pricingPackagesUpdateFailed = false;
        draft.pricingPackagesUpdateError = null;
        draft.pricingPackages = action.packages;
        break;

      case SHOWROOM_UPDATE_PRICING_PACKAGES_FAILED:
        draft.pricingPackagesUpdating = false;
        draft.pricingPackagesUpdateFailed = true;
        draft.pricingPackagesUpdateError = getActionError(
          action,
          'Pricing packages could not be saved'
        );
        break;

      // ========================
      // Admin actions
      // ========================
      case SHOWROOM_PUBLISH_EVENT:
        draft.currentEventFetching = true;
        draft.currentEventSuccess = false;
        draft.currentEventFailed = false;
        draft.currentEventError = null;
        break;

      case SHOWROOM_PUBLISH_EVENT_SUCCESS:
        draft.currentEventFetching = false;
        draft.currentEventSuccess = true;
        draft.currentEventFailed = false;
        draft.currentEventError = null;
        draft.currentEvent = action.event;
        draft.events = updateEventInList(draft.events, action.event);
        break;

      case SHOWROOM_PUBLISH_EVENT_FAILED:
        draft.currentEventFetching = false;
        draft.currentEventSuccess = false;
        draft.currentEventFailed = true;
        draft.currentEventError = getActionError(
          action,
          'Show room could not be published'
        );
        break;

      case SHOWROOM_UNPUBLISH_EVENT:
        draft.currentEventFetching = true;
        draft.currentEventSuccess = false;
        draft.currentEventFailed = false;
        draft.currentEventError = null;
        break;

      case SHOWROOM_UNPUBLISH_EVENT_SUCCESS:
        draft.currentEventFetching = false;
        draft.currentEventSuccess = true;
        draft.currentEventFailed = false;
        draft.currentEventError = null;
        draft.currentEvent = action.event;
        draft.events = updateEventInList(draft.events, action.event);
        break;

      case SHOWROOM_UNPUBLISH_EVENT_FAILED:
        draft.currentEventFetching = false;
        draft.currentEventSuccess = false;
        draft.currentEventFailed = true;
        draft.currentEventError = getActionError(
          action,
          'Show room could not be unpublished'
        );
        break;

      case SHOWROOM_DELETE_EVENT:
        draft.eventDeleteSubmitting = true;
        draft.eventDeleteSuccess = false;
        draft.eventDeleteFailed = false;
        draft.eventDeleteError = null;
        break;

      case SHOWROOM_DELETE_EVENT_SUCCESS:
        draft.eventDeleteSubmitting = false;
        draft.eventDeleteSuccess = true;
        draft.eventDeleteFailed = false;
        draft.eventDeleteError = null;
        draft.events = draft.events.filter(
          (event) => String(event._id) !== String(action.eventId)
        );
        draft.eventsTotal = Math.max(0, draft.eventsTotal - 1);
        if (
          draft.currentEvent &&
          String(draft.currentEvent._id) === String(action.eventId)
        ) {
          draft.currentEvent = null;
        }
        break;

      case SHOWROOM_DELETE_EVENT_FAILED:
        draft.eventDeleteSubmitting = false;
        draft.eventDeleteSuccess = false;
        draft.eventDeleteFailed = true;
        draft.eventDeleteError =
          action.err?.message || action.err?.error || 'Show room delete failed';
        break;

      case SHOWROOM_ADJUST_SEATS:
        draft.currentEventFetching = true;
        draft.currentEventSuccess = false;
        draft.currentEventFailed = false;
        draft.currentEventError = null;
        break;

      case SHOWROOM_ADJUST_SEATS_SUCCESS:
        draft.currentEventFetching = false;
        draft.currentEventSuccess = true;
        draft.currentEventFailed = false;
        draft.currentEventError = null;
        draft.currentEvent = action.event;
        draft.events = updateEventInList(draft.events, action.event);
        break;

      case SHOWROOM_ADJUST_SEATS_FAILED:
        draft.currentEventFetching = false;
        draft.currentEventSuccess = false;
        draft.currentEventFailed = true;
        draft.currentEventError = getActionError(
          action,
          'Show room seats could not be updated'
        );
        break;

      case SHOWROOM_ADMIN_REVIEW_EVENT:
        draft.reviewSubmitting = true;
        draft.reviewSuccess = false;
        draft.reviewFailed = false;
        draft.reviewError = null;
        break;

      case SHOWROOM_ADMIN_REVIEW_EVENT_SUCCESS:
        draft.reviewSubmitting = false;
        draft.reviewSuccess = true;
        draft.reviewFailed = false;
        draft.reviewError = null;
        draft.currentEvent = action.event;
        draft.events = updateEventInList(draft.events, action.event);
        break;

      case SHOWROOM_ADMIN_REVIEW_EVENT_FAILED:
        draft.reviewSubmitting = false;
        draft.reviewSuccess = false;
        draft.reviewFailed = true;
        draft.reviewError = action.err?.message || action.err?.error || 'Review submission failed';
        break;

      // ========================
      // Admin member passes
      // ========================
      case SHOWROOM_BATCH_FREE_MEMBER_PASS:
        draft.memberPassesFetching = true;
        draft.memberPassesSuccess = false;
        draft.memberPassesFailed = false;
        break;

      case SHOWROOM_BATCH_FREE_MEMBER_PASS_SUCCESS:
        draft.memberPassesFetching = false;
        draft.memberPassesSuccess = true;
        draft.memberPassesFailed = false;
        draft.memberPasses = [...(action.result?.memberPasses || []), ...draft.memberPasses];
        break;

      case SHOWROOM_BATCH_FREE_MEMBER_PASS_FAILED:
        draft.memberPassesFetching = false;
        draft.memberPassesSuccess = false;
        draft.memberPassesFailed = true;
        break;

      // ========================
      // UI State
      // ========================
      case SHOWROOM_SET_ACTIVE_TAB:
        draft.activeTab = action.tab;
        break;

      case SHOWROOM_CLEAR_CURRENT_EVENT:
        draft.currentEvent = null;
        draft.currentEventFetching = false;
        draft.currentEventSuccess = false;
        draft.currentEventFailed = false;
        draft.currentEventError = null;
        break;
    }
  });

export default showroomReducer;
